/**
 * §6 — Team-level tactics.
 *
 * Coaches set a team-wide approach: build-up style + defensive shape, plus a
 * row of sliders (pressing, width, depth, tempo, aggression, line height).
 * They also have up to 5 named "tactic slots" they can switch between in-
 * match (think EAFC custom tactics) — each slot is a full
 * {@link TeamTacticsConfig} snapshot.
 *
 * This module owns the concrete shape that fills the
 * `TacticsTeamTacticsBundle` slot reserved on `TacticsLayout` (see
 * `./types.ts`). The bundle is per-team (home + away) so coaches can prep
 * and the opposition manager can have independent setups.
 *
 * All mutating helpers return a NEW bundle — never mutate in place.
 * Sliders are clamped to [0, 100] so out-of-range input from the wire or
 * a UI bug can't poison the bundle.
 */

// ---------------------------------------------------------------------------
// Enums + slider definitions
// ---------------------------------------------------------------------------

export const BUILD_UP_STYLES = [
  'short-pass',
  'balanced',
  'direct',
  'long-ball',
] as const;
export type BuildUpStyle = (typeof BUILD_UP_STYLES)[number];

export const DEFENSIVE_APPROACHES = [
  'low-block',
  'mid-block',
  'high-press',
  'counter-press',
] as const;
export type DefensiveApproach = (typeof DEFENSIVE_APPROACHES)[number];

/** A single 0–100 slider on the team-tactics panel. The `key` is the
 *  identifier used inside `TeamTacticsConfig.sliders`; `label` is the
 *  human-readable name surfaced in the HUD. Defaults sit at 50 so a
 *  fresh slot is intentionally "balanced" — the coach must dial in. */
export interface SliderField {
  key: string;
  label: string;
  min: 0;
  max: 100;
  default: 50;
}

export const SLIDER_FIELDS: readonly SliderField[] = [
  { key: 'pressingIntensity', label: 'Pressing Intensity', min: 0, max: 100, default: 50 },
  { key: 'width',             label: 'Width',              min: 0, max: 100, default: 50 },
  { key: 'depth',             label: 'Depth',              min: 0, max: 100, default: 50 },
  { key: 'tempo',             label: 'Tempo',              min: 0, max: 100, default: 50 },
  { key: 'aggression',        label: 'Aggression',         min: 0, max: 100, default: 50 },
  { key: 'lineHeight',        label: 'Line Height',        min: 0, max: 100, default: 50 },
];

// ---------------------------------------------------------------------------
// Config + slot + bundle types
// ---------------------------------------------------------------------------

/** Snapshot of the team-wide tactical knobs for one slot. */
export interface TeamTacticsConfig {
  buildUpStyle: BuildUpStyle;
  defensiveApproach: DefensiveApproach;
  /** Slider values keyed by `SliderField.key`. Always in [0, 100]. */
  sliders: Record<string, number>;
}

/** One of (up to) five named tactic slots the coach can switch between. */
export interface TacticsSlot {
  id: string;
  name: string;
  tactics: TeamTacticsConfig;
}

/** Per-team slot library + the currently active slot id. */
export interface TeamTacticsSide {
  slots: TacticsSlot[];
  activeSlotId: string;
}

/** Full home + away tactics bundle — this is what gets stored on
 *  `TacticsLayout.teamTactics`. */
export interface TeamTacticsBundle {
  home: TeamTacticsSide;
  away: TeamTacticsSide;
}

// Default slot names — match the seven-words-or-less style coaches use
// when calling tactics from the touchline.
export const DEFAULT_SLOT_NAMES = [
  'Default',
  'Chase Game',
  'Park the Bus',
  'Possession',
  'Counter',
] as const;

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

/** Balanced defaults: 'balanced' build-up + 'mid-block' defence + every
 *  slider at 50. The HUD seeds new slots from this. */
export function defaultTacticsConfig(): TeamTacticsConfig {
  const sliders: Record<string, number> = {};
  for (const field of SLIDER_FIELDS) sliders[field.key] = field.default;
  return {
    buildUpStyle: 'balanced',
    defensiveApproach: 'mid-block',
    sliders,
  };
}

/** Generate a stable-ish id from a slot name + the current timestamp.
 *  We intentionally don't pull in a uuid dep — slot ids are local to a
 *  single layout and collisions are vanishingly rare. */
export function defaultSlot(name: string): TacticsSlot {
  // Slug the name so the id stays readable in devtools and logs; the
  // random suffix keeps two slots with the same name from colliding.
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const rand = Math.random().toString(36).slice(2, 7);
  const id = `slot-${slug || 'unnamed'}-${Date.now().toString(36)}-${rand}`;
  return { id, name, tactics: defaultTacticsConfig() };
}

/** Generate the launch bundle: 5 default slots per team with the first
 *  one active. */
export function defaultBundle(): TeamTacticsBundle {
  const buildSide = (): TeamTacticsSide => {
    const slots = DEFAULT_SLOT_NAMES.map((n) => defaultSlot(n));
    return { slots, activeSlotId: slots[0]!.id };
  };
  return { home: buildSide(), away: buildSide() };
}

// ---------------------------------------------------------------------------
// Immutable mutators
// ---------------------------------------------------------------------------

/** Switch the active slot for one team. Returns the bundle unchanged if
 *  `slotId` doesn't exist on that side — defensive against stale ids
 *  surviving in URL params or persisted state. */
export function setActiveSlot(
  bundle: TeamTacticsBundle,
  team: 'home' | 'away',
  slotId: string,
): TeamTacticsBundle {
  const side = bundle[team];
  if (!side.slots.some((s) => s.id === slotId)) return bundle;
  return {
    ...bundle,
    [team]: { ...side, activeSlotId: slotId },
  };
}

/** Clamp helper — keeps a slider in [0, 100] regardless of caller input. */
function clampSlider(value: number): number {
  if (!Number.isFinite(value)) return 50;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

/** Set a slider value on the given team's slot. Returns the bundle
 *  unchanged when the slot id is unknown (same defensive stance as
 *  setActiveSlot). */
export function updateSlider(
  bundle: TeamTacticsBundle,
  team: 'home' | 'away',
  slotId: string,
  key: string,
  value: number,
): TeamTacticsBundle {
  const side = bundle[team];
  const idx = side.slots.findIndex((s) => s.id === slotId);
  if (idx === -1) return bundle;
  const slot = side.slots[idx]!;
  const nextSliders = { ...slot.tactics.sliders, [key]: clampSlider(value) };
  const nextSlot: TacticsSlot = {
    ...slot,
    tactics: { ...slot.tactics, sliders: nextSliders },
  };
  const nextSlots = [...side.slots];
  nextSlots[idx] = nextSlot;
  return {
    ...bundle,
    [team]: { ...side, slots: nextSlots },
  };
}

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

function isConfig(value: unknown): value is TeamTacticsConfig {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!BUILD_UP_STYLES.includes(v.buildUpStyle as BuildUpStyle)) return false;
  if (!DEFENSIVE_APPROACHES.includes(v.defensiveApproach as DefensiveApproach)) return false;
  if (!v.sliders || typeof v.sliders !== 'object') return false;
  return true;
}

function isSlot(value: unknown): value is TacticsSlot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string' || v.id.length === 0) return false;
  if (typeof v.name !== 'string') return false;
  return isConfig(v.tactics);
}

function isSide(value: unknown): value is TeamTacticsSide {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.activeSlotId !== 'string') return false;
  if (!Array.isArray(v.slots)) return false;
  return v.slots.every(isSlot);
}

/** Narrow an unknown bundle (e.g. read from localStorage) to a
 *  {@link TeamTacticsBundle}. Strict — rejects empty sides, unknown
 *  enum values, and slots missing required fields. */
export function isTeamTacticsBundle(value: unknown): value is TeamTacticsBundle {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return isSide(v.home) && isSide(v.away);
}
